<template>
  <section class="UniversityLoadingExternalSearchResults" v-if="portalType === PortalType.MASTER">
    <h2 :class="{ Blink: true }">Redirecting to University Search...</h2>
  </section>
  <template v-else>
    <template v-if="!isError">
        <div
            v-if="isLoading || (searchStore?.pageData?.results?.length ?? 0) > 0"
            class="UniversityCards"
        >
            <template
                v-for="(item, index) in universities"
                :key="itemKey(item, index)"
            >
                <OrganisationCard
                    :organisation="item"
                    :loading="isLoading"
                    :position="index"
                    :disciplineName="disciplineName"
                />
            </template>
        </div>
        <NoResults/>
        <Pagination />
        <Faqs />
    </template>

    <ErrorResult v-else/>
  </template>
</template>

<script setup lang="ts">
import { SearchResultItem } from "@app/ad-manager/utils/banner-positions";
import IPersonalizedOrganisationCard from "@shared/interfaces/dtos/IPersonalizedOrganisationCard";
import OrganisationCard from './OrganisationCard/OrganisationCard.vue';
import { useSearchStore } from "@stores/useSearchStore";
import {
  disciplineGetSubOrParentDiscipline
} from "@studyportals/static-domain-data/disciplines/discipline-get-sub-or-parent-discipline";
import { computed, ref } from "vue";
import Pagination from '../Pagination/Pagination.vue';
import NoResults from "../NoResults/NoResults.vue";
import ErrorResult from "@app/components/ErrorResult/ErrorResult.vue";
import Faqs from '../Faqs/Faqs.vue';
import { getPortalTypeFromEnvClient } from "@utils/get-portal-type-from-env.client.ts";
import { PortalType } from "@studyportals/domain-client";

const skeletonCardCount = 20;
const searchStore = useSearchStore();

const isError = computed(() => searchStore.pageData?.isError ?? false);

const universities = computed<(IPersonalizedOrganisationCard | undefined)[]>(() => {
	if (searchStore.loading || !searchStore.pageData?.results) {
		return Array.from({ length: skeletonCardCount }, () => undefined);
	}

	return searchStore.pageData.results;
});


const itemKey = (item: SearchResultItem<IPersonalizedOrganisationCard | undefined>, index: number): string => {
	const organisation = item as IPersonalizedOrganisationCard | undefined;
	return `organisation-${organisation?.id ?? `skeleton-organisation-${index}`}`;
};

const isLoading = computed(() => searchStore.loading);

const disciplineName = computed(() => {
    if (!searchStore.pageData?.context?.rankedDisciplineId) {
        return undefined;
    }

	const discipline = disciplineGetSubOrParentDiscipline(searchStore.pageData?.context?.rankedDisciplineId ?? '');

	return discipline?.name ?? undefined;
});

const portalType = getPortalTypeFromEnvClient();

</script>

<style scoped lang="scss">

    .UniversityCards {
        display: flex;
		flex-direction: column;
		gap: 1rem;
    }

    .Title {
        color: $Grey-900;
        @include TextStyle(HeadingSmall);

        @include Breakpoint(Large, ExtraLarge) {
            @include TextStyle(HeadingMedium);
        }
    }

    .UniversityLoadingExternalSearchResults {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 0.5rem;
      @include PaddingAreas(0.25, 0);
    }
    .Blink {
      animation: blink-animation 1s linear infinite;
    }

    @keyframes blink-animation {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0;
      }
    }
</style>
